/**
 * Shared action-row composition (#1466 / ADR F-30).
 *
 * The host supplies every affordance after its governed source has resolved.
 * This component owns hierarchy and responsive/sticky layout only: secondary
 * actions precede one visually final primary action. No supplied actions and
 * no status means no bar in the DOM.
 */
import type { ReactNode } from 'react'
import { Button } from '../controls/Button'
import s from './ActionBar.module.css'

export interface ActionBarProps {
  status?: ReactNode
  notice?: ReactNode
  secondaryActions?: ReactNode
  primaryAction?: ReactNode
  sticky?: boolean
  label?: string
  testId?: string
  className?: string
}

export function ActionBar({
  status,
  notice,
  secondaryActions,
  primaryAction,
  sticky = false,
  label = 'Available actions',
  testId,
  className,
}: ActionBarProps) {
  if (status == null && notice == null && secondaryActions == null && primaryAction == null) return null
  return (
    <section
      className={[s.bar, className].filter(Boolean).join(' ')}
      data-sticky={sticky || undefined}
      data-testid={testId}
      aria-label={label}
    >
      {(status != null || notice != null) && (
        <div className={s.copy}>
          {status != null && <div className={s.status}>{status}</div>}
          {notice != null && <div className={s.notice}>{notice}</div>}
        </div>
      )}
      {(secondaryActions != null || primaryAction != null) && (
        <div className={s.actions}>
          {secondaryActions}
          {primaryAction}
        </div>
      )}
    </section>
  )
}

export interface SaveBarProps {
  state: 'new' | 'dirty'
  /**
   * Override the default "New record" / "Unsaved changes" line.
   *
   * A settings form has more to say than dirty-or-not — it is also clean,
   * saving, saved, blocked by validation, or refused — and a bar that says
   * "Unsaved changes" over a clean form is simply wrong. Optional, so the two
   * record-editing callers keep the sentence they have.
   */
  status?: ReactNode
  saving?: boolean
  saveDisabled?: boolean
  saveLabel?: ReactNode
  /** Add the second native submit intent used by governed record editors. */
  saveAndClose?: boolean
  saveAndCloseLabel?: ReactNode
  discardLabel?: ReactNode
  onSave?: () => void
  onDiscard: () => void
  notice?: ReactNode
  testId?: string
  sticky?: boolean
}

/**
 * The ordinary direct-write form action row. Do not render it when the source
 * write door is unavailable; SaveBar does not turn a refusal into authority.
 */
export function SaveBar({
  state,
  status,
  saving = false,
  saveDisabled = false,
  saveLabel = 'Save changes',
  saveAndClose = false,
  saveAndCloseLabel = 'Save & close',
  discardLabel = 'Discard',
  onSave,
  onDiscard,
  notice,
  testId,
  sticky = true,
}: SaveBarProps) {
  return (
    <ActionBar
      sticky={sticky}
      testId={testId}
      label={saveAndClose ? 'Save record' : 'Save changes'}
      status={status ?? <span className={s.unsaved}>{state === 'new' ? 'New record' : 'Unsaved changes'}</span>}
      notice={notice}
      /* The controls are addressable when the bar is. `testId` used to land
         only on the wrapping section, so no test could name the control it was
         actually clicking — every caller invented its own testid on a button
         beside the bar instead. Derived rather than two more props: one name
         for the bar gives one name per button, and a caller that passes no
         `testId` is unchanged. */
      secondaryActions={<>
        <Button
          type="button" variant="ghost" onClick={onDiscard} disabled={saving}
          data-testid={testId ? `${testId}-discard` : undefined}
        >
          {discardLabel}
        </Button>
        {saveAndClose && (
          <Button
            type="submit" name="save-intent" value="close" variant="secondary"
            disabled={saving || saveDisabled}
            data-testid={testId ? `${testId}-save-close` : undefined}
          >
            {saveAndCloseLabel}
          </Button>
        )}
      </>}
      primaryAction={(
        <Button
          type={onSave == null ? 'submit' : 'button'}
          name={saveAndClose && onSave == null ? 'save-intent' : undefined}
          value={saveAndClose && onSave == null ? 'stay' : undefined}
          variant="primary"
          loading={saving}
          disabled={saveDisabled}
          onClick={onSave}
          data-testid={testId ? `${testId}-save` : undefined}
        >
          {saveLabel}
        </Button>
      )}
    />
  )
}
